import React, { createContext, useContext, useMemo, useState, ReactNode } from 'react';
import { messages, Language } from '../i18n/messages';
import { defaultLanguageBySite, detectSiteMode } from '../utils/site';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType>({} as LanguageContextType);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    if (saved === 'zh-CN' || saved === 'en-US') return saved;
    return defaultLanguageBySite(detectSiteMode());
  });

  const setLanguage = (nextLanguage: Language) => {
    setLanguageState(nextLanguage);
    localStorage.setItem('language', nextLanguage);
  };

  const t = (key: string, vars?: Record<string, string | number>) => {
    let text = messages[language][key] ?? messages['zh-CN'][key] ?? key;
    if (vars) {
      Object.entries(vars).forEach(([name, value]) => {
        text = text.replace(`{${name}}`, String(value));
      });
    }
    return text;
  };

  const value = useMemo(() => ({ language, setLanguage, t }), [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
